import { FieldPath, FieldPathValue, FieldValues } from 'react-hook-form';
import { Bareme, BaremeIndemnisationValues } from 'src/types/bareme.type';
import {
  EditDatesOptions,
  PrejudiceFormBase,
  PrejudiceType,
} from 'src/types/prejudice.type';
import { Procedure } from 'src/types/procedure.type';
import { Victime } from 'src/types/victime.type';
import { prejudicesData } from '../prejudices';

export const prejudiceBaseDefaultValues = ({
  values,
}: {
  values: Partial<PrejudiceFormBase> | undefined;
}): PrejudiceFormBase => ({
  notes: values?.notes || '',
  chiffrage: values?.chiffrage || 'en_cours',
  formVersion: values?.formVersion || 0,
  editedFields: values?.editedFields || [],
  defaultDatesValues: values?.defaultDatesValues || {},
});
export type BaremeDefaultMontant = Bareme & {
  values: BaremeIndemnisationValues[];
};
export const getAvailablePredefinedDates = (params: {
  procedure: Pick<Procedure, 'dateConsolidation' | 'dateLiquidation'>;
  victime: Pick<Victime, 'dateAccident' | 'dateDeces'>;
  prejudiceType: PrejudiceType;
}): EditDatesOptions[] => {
  const { procedure, victime, prejudiceType } = params;
  const { dateAccident, dateDeces, dateConsolidation, dateLiquidation } = {
    dateAccident: victime.dateAccident
      ? new Date(victime.dateAccident)
      : undefined,
    dateDeces: victime.dateDeces ? new Date(victime.dateDeces) : undefined,
    dateConsolidation: procedure.dateConsolidation
      ? new Date(procedure.dateConsolidation)
      : undefined,
    dateLiquidation: procedure.dateLiquidation
      ? new Date(procedure.dateLiquidation)
      : undefined,
  };

  const availableDates: EditDatesOptions[] = [];

  if (dateAccident) availableDates.push('dateAccident');
  if (dateDeces) availableDates.push('dateDeces');
  if (dateConsolidation) availableDates.push('dateConsolidation');
  if (dateLiquidation) availableDates.push('dateLiquidation');

  const prejudiceCategory =
    prejudicesData[prejudiceType || 'DEPENSE_SANTE_FUTURES']?.categorie;
  if (!prejudiceCategory) return availableDates;

  switch (prejudiceCategory) {
    case 'PREJUDICES_PATRIMONIAUX_TEMPORAIRES':
    case 'PREJUDICES_EXTRA_PATRIMONIAUX_TEMPORAIRES':
      return availableDates.filter((date) => {
        switch (date) {
          case 'dateAccident':
          case 'dateConsolidation':
            return true;
          case 'dateDeces':
          case 'dateLiquidation':
            // if dateConsolidation is defined, user cannot choose dateDeces or dateLiquidation, because dateConsolidation becomes the max date
            return !dateConsolidation;
        }
      });
    case 'PREJUDICES_PATRIMONIAUX_PERMANENTS':
    case 'PREJUDICES_EXTRA_PATRIMONIAUX_PERMANENTS':
      return availableDates.filter((date) => {
        switch (date) {
          case 'dateConsolidation':
          case 'dateLiquidation':
          case 'dateDeces':
            return true;
          case 'dateAccident':
            // if dateConsolidation is defined, user cannot choose dateAccident, because dateConsolidation becomes the min date
            return !dateConsolidation;
        }
      });

    case 'AUTRES_PREJUDICES_HORS_CONSOLIDATION':
    case 'POSTES_ANNEXES':
    case 'VICTIMES_INDIRECTES_PATRIMONIAUX':
    default:
      return availableDates;
  }
};

export const getDefaultFieldValueIfEdited = <
  TFormValues extends FieldValues,
  TFieldPath extends FieldPath<TFormValues>,
  TValue extends FieldPathValue<TFormValues, TFieldPath>,
>(params: {
  values: Partial<TFormValues> | undefined;
  field: TFieldPath;
  defaultValue: TValue;
}): TValue | undefined => {
  const { values, field, defaultValue } = params;
  if (values?.editedFields?.includes(field)) {
    return values[field];
  }
  return defaultValue;
};
